import React from "react";
import styled from "styled-components";

import * as u from "../utils";

export default function SelectSearch(props) {
  const innerRef = React.useRef(null);
  const [subTxt, setSubTxt] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  React.useEffect(() => attachOutsideClick(), [isOpen]);

  return (
    <S.Container
      isInvalid={props.isInvalid}
      onClick={props.onClick}
      isDisabled={props.disabled}
      required={props.required}
      ref={innerRef}
      data-cy={`${props.name}-select`}
    >
      {!isOpen && (
        <S.Placeholder
          onClick={() => setIsOpen(true)}
          hasValue={!!props.value}
          readonly={props.disabled}
        >
          {props.value || props.hint || ""}
        </S.Placeholder>
      )}
      {isOpen && (
        <React.Fragment>
          <S.Input
            ref={(elm) => elm && elm.focus()}
            placeholder={"Type to search"}
            value={subTxt}
            onChange={(e) => setSubTxt(e.target.value)}
            data-cy={`${props.name}-select-input`}
          />
          <S.OptionsContainer>{getMaybeOptions()}</S.OptionsContainer>
        </React.Fragment>
      )}
    </S.Container>
  );

  function attachOutsideClick() {
    if (!isOpen) return;
    document.addEventListener("click", onMaybeOutside);
    return () => document.removeEventListener("click", onMaybeOutside);

    function onMaybeOutside(evt) {
      if (innerRef?.current?.contains(evt.target)) return;
      setIsOpen(false);
    }
  }

  function getMaybeOptions() {
    const terms = subTxt.toLowerCase().trim().split(" ").filter(Boolean);
    const opts = props.opts?.filter(byIncludedInSearch) || [];
    return opts.length ? opts.map(getOption) : <div>No options</div>;

    function byIncludedInSearch(elm) {
      const words = elm.toLowerCase().trim().split(" ").filter(Boolean);
      const isIncludedInSearch = terms.every((term) =>
        words.some((word) => word.startsWith(term))
      );
      return !subTxt || isIncludedInSearch;
    }
  }

  function getOption(elm) {
    const onClick = () => {
      props.onSelect(elm);
      setSubTxt("");
      setIsOpen(false);
    };

    const subProps = {
      key: elm,
      isActive: elm === props.value,
      onClick,
    };

    return <S.Option {...subProps}>{elm}</S.Option>;
  }
}

const S = {};

S.Container = styled.div`
  border: ${(p) =>
    p.isInvalid || p.error
      ? "1px solid " + u.colours.errorRed
      : p.required
        ? "2px solid " + u.colours.pillTeal
        : "1px solid " + u.colours.shellOverlay};
  border-radius: 5px;
  margin-bottom: 20px;
  position: relative;
  ${(p) =>
    p.isDisabled &&
    `
    pointer-events: none;
    border: none;
  `}
`;

S.Placeholder = styled.div`
  align-items: center;
  background-color: white;
  border-radius: 5px;
  color: ${(props) =>
    props.readonly ? "rgb(152, 152, 152)" : u.colours.charcoal};
  display: flex;
  font-size: 15px;
  height: 59px;
  letter-spacing: 0.5px;
  outline: none;
  padding-left: 16px;
  :hover {
    cursor: pointer;
  }
`;

S.Input = styled.input`
  border: none;
  border-radius: 5px;
  color: ${(props) => (props.readonly ? "rgb(152, 152, 152)" : "black")};
  font-size: 1.5rem;
  height: 59px;
  outline: none;
  padding-left: 16px;
  width: 100%;
  &::placeholder {
    color: rgb(170, 170, 170);
    font-weight: 300;
    letter-spacing: 0.5px;
  }
`;

S.OptionsContainer = styled.ul`
  background-color: white;
  border-radius: 5px;
  box-shadow:
    rgba(0, 0, 0, 0.2) 0px 12px 28px 0px,
    rgba(0, 0, 0, 0.1) 0px 2px 4px 0px,
    rgba(255, 255, 255, 0.5) 0px 0px 0px 1px inset;
  max-height: 250px;
  overflow-y: scroll;
  overflow-x: none;
  padding: 10px 10px;
  position: absolute;
  top: 60px;
  width: 100%;
  z-index: 1;
`;

S.Option = styled.div`
  align-items: center;
  border-radius: 4px;
  display: flex;
  font-size: 15px;
  height: 40px;
  padding: 2.5px 0 2.5px 5px;
  ${(props) =>
    props.isActive
      ? `
    background-color: ${u.colours.pillTeal};
    font-weight: 500;
  `
      : ""};
  :hover {
    cursor: pointer;
    background-color: ${u.colours.shell};
  }
`;
